import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import type { DropdownMenuContentProps } from "@radix-ui/react-dropdown-menu";
import { Home, Menu, Settings, UserSearch } from "lucide-react";
import type { ReactNode } from "react";
import { useLocation } from "wouter";
import { useContactProfilesEnabled } from "../../hooks/useContactProfilesEnabled.ts";
import { cn } from "../../lib/utils.ts";
import { OkthxLogo, OkThxWordmark } from "../icons/Logo.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip.tsx";

/**
 * The app's primary navigation menu — Home, Settings. (Routines,
 * Memories, Skills, Library, and gated feature links live inside Settings.) Extracted from
 * `LogoNav` so the same menu can hang off any trigger — currently the header
 * hamburger (`LogoNav`). The asides reach settings directly instead, through
 * `SettingsNavButton`.
 *
 * The calendar surface's mobile hamburger is a separate component: the left
 * nav sheet in `AppNavMenuButton.tsx`, which carries the promoted destinations
 * (Connectors, Search) mobile-only. This dropdown still renders at mobile
 * widths on headers without `includeBackButton`/`hideNav`.
 *
 * `children` is rendered inside the `DropdownMenu` and must contain a
 * `DropdownMenuTrigger`. Content placement (`align`/`side`/`sideOffset`) is
 * forwarded to the menu so callers can open it downward (header) or upward
 * (sidebar footer).
 */
export const MainNavMenu = ({
  children,
  align = "start",
  side,
  sideOffset = 14,
  contentClassName,
  showWordmark = false,
  menuLogoClassName,
}: {
  /** Trigger element(s). Must include a `DropdownMenuTrigger`. */
  children: ReactNode;
  align?: DropdownMenuContentProps["align"];
  side?: DropdownMenuContentProps["side"];
  sideOffset?: number;
  contentClassName?: string;
  /** Show the wordmark header row at the top of the menu (header, small screens). */
  showWordmark?: boolean;
  /** Controls when the wordmark row inside the menu is hidden. Defaults to "min-[28rem]:hidden". */
  menuLogoClassName?: string;
}) => {
  const [, setLocation] = useLocation();
  const { enabled: peopleEnabled } = useContactProfilesEnabled();

  return (
    <DropdownMenu>
      {children}
      <DropdownMenuContent
        align={align}
        side={side}
        className={cn("gap-2 w-55", contentClassName)}
        sideOffset={sideOffset}
      >
        {showWordmark && (
          <DropdownMenuGroup className={menuLogoClassName ?? "min-[28rem]:hidden"}>
            <DropdownMenuLabel className="pt-3 pb-2.5 px-2">
              <OkThxWordmark className="scale-75 origin-left" />
            </DropdownMenuLabel>
          </DropdownMenuGroup>
        )}
        <DropdownMenuItem className="py-2.5" onSelect={() => setLocation("/")}>
          <Home className="size-4 stroke-2" /> Home
        </DropdownMenuItem>
        {peopleEnabled && (
          <DropdownMenuItem className="py-2.5" onSelect={() => setLocation("/people")}>
            <UserSearch className="size-4 stroke-2" /> People
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="py-0" />
        <DropdownMenuItem className="py-2.5" onSelect={() => setLocation("/settings")}>
          <Settings className="size-4 stroke-2" /> Settings
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const LogoNav = ({
  className,
  useWordmark,
  menuOnly,
  logoClassName,
  menuLogoClassName,
}: {
  className?: string;
  useWordmark?: boolean;
  menuOnly?: boolean;
  logoClassName?: string;
  /** Controls when the wordmark row inside the dropdown menu is hidden. Defaults to "min-[28rem]:hidden". */
  menuLogoClassName?: string;
}) => {
  const [, setLocation] = useLocation();

  const handleLogoClick = () => {
    setLocation("/");
  };

  const LogoComponent = useWordmark ? (
    <OkThxWordmark
      className={logoClassName}
      cursor="pointer"
      pointerEvents="auto"
      role="button"
      onClick={handleLogoClick}
    />
  ) : (
    <OkthxLogo
      className={cn("size-6", logoClassName)}
      onClick={handleLogoClick}
      cursor="pointer"
      pointerEvents="auto"
      role="button"
    />
  );

  return (
    <MainNavMenu showWordmark menuLogoClassName={menuLogoClassName} sideOffset={14}>
      <div className={cn("flex flex-row items-center gap-4", className)}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Menu className="size-6 cursor-pointer" />
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">Main menu</TooltipContent>
        </Tooltip>
        {!menuOnly && LogoComponent}
      </div>
    </MainNavMenu>
  );
};
